import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { authClient } from "../lib/auth-client.ts";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/vroom/brand-mark";
import { FormField } from "@/components/vroom/form-field";

// Auth state for /two-factor:
// - Full session present → already past 2FA; if twoFactorEnabled, go to /dashboard,
//   otherwise the user still needs to enroll → /onboarding/mfa.
// - No session → almost certainly the post-credential 2FA-pending state; render
//   the form. (The pending cookie is httpOnly so we can't read it from JS — if
//   it's missing too, verifyTotp will 401 with INVALID_TWO_FACTOR_COOKIE and the
//   submit handler bounces to /sign-in.)
export const Route = createFileRoute("/two-factor")({
  beforeLoad: async () => {
    const { data: session } = await authClient.getSession();
    if (session?.user) {
      if (session.user.twoFactorEnabled) {
        throw redirect({ to: "/dashboard" });
      }
      throw redirect({ to: "/onboarding/mfa" });
    }
  },
  component: TwoFactorPage,
});

function TwoFactorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (code.length !== 6) {
      setError(t("two-factor.form.errors.code-length"));
      return;
    }
    setPending(true);
    const result = await authClient.twoFactor.verifyTotp({ code });
    setPending(false);
    if (result.error) {
      const errCode = (result.error as { code?: string }).code;
      if (errCode === "INVALID_TWO_FACTOR_COOKIE") {
        await navigate({ to: "/sign-in" });
        return;
      }
      setError(result.error.message ?? t("two-factor.form.errors.invalid"));
      return;
    }
    await navigate({ to: "/dashboard" });
  }

  async function onSignOut() {
    await navigate({ to: "/sign-out" });
  }

  return (
    <main className="mx-auto mt-20 grid w-full max-w-sm gap-6 px-4">
      <div className="flex items-center gap-2">
        <BrandMark />
        <span
          className="text-[18px] font-semibold text-ink-900"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {t("common.brand.label")}
        </span>
      </div>
      <h1
        className="text-[22px] font-semibold text-ink-900"
        style={{ fontFamily: "var(--font-display)" }}
      >
        {t("two-factor.title")}
      </h1>
      <p className="text-[13px] text-ink-700">{t("two-factor.description")}</p>
      <form onSubmit={onSubmit} className="grid gap-4">
        <FormField
          id="two-factor-code"
          label={t("two-factor.form.code.label")}
          error={error ?? undefined}
          inputProps={{
            type: "text",
            inputMode: "numeric",
            autoComplete: "one-time-code",
            maxLength: 6,
            value: code,
            onChange: (e) => setCode(e.currentTarget.value.replaceAll(/[^0-9]/g, "")),
            required: true,
            autoFocus: true,
          }}
        />
        <Button type="submit" variant="primary" loading={pending} className="justify-center">
          {t("two-factor.form.submit.label")}
        </Button>
        <Button
          type="button"
          variant="default"
          className="justify-center"
          onClick={() => void onSignOut()}
        >
          {t("common.nav.sign-out.label")}
        </Button>
      </form>
    </main>
  );
}
